import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building } from '../types/building';
import { BuildingDataService } from '../services/buildingDataService';
import BuildingList from './BuildingList';
import RetrofitCalculator from './RetrofitCalculator';
import DefinitionsAndTAM from './DefinitionsAndTAM';

interface PitchProps {
    buildings: Building[];
    loading: boolean;
    error: string | null;
}

const Pitch: React.FC<PitchProps> = ({ buildings, loading, error }) => {
    const [analysisComplete, setAnalysisComplete] = useState(false);
    const [summaryStats, setSummaryStats] = useState<any>(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [presentationMode, setPresentationMode] = useState(false);
    const navigate = useNavigate();

    // Slide navigation functions
    const nextSlide = () => {
        if (currentSlide < 8) { // 9 slides (0-8)
            setCurrentSlide(currentSlide + 1);
        }
    };

    const prevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    const goToSlide = (slideIndex: number) => {
        setCurrentSlide(slideIndex);
    };

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (presentationMode) {
                if (event.key === 'ArrowRight' || event.key === ' ') {
                    event.preventDefault();
                    nextSlide();
                } else if (event.key === 'ArrowLeft') {
                    event.preventDefault();
                    prevSlide();
                } else if (event.key === 'Escape') {
                    setPresentationMode(false);
                }
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [presentationMode, currentSlide]);

    const slideNames = [
        "Title & Executive Summary",
        "The Problem", 
        "The Solution",
        "Market Opportunity & Business Model",
        "Traction & Validation",
        "Competitive Advantage & Go-to-Market",
        "Financial Projections & Growth Plan",
        "Team & Use of Funds",
        "The Ask & Next Steps"
    ];

    useEffect(() => {
        console.log('🏢 Dashboard useEffect - buildings.length:', buildings.length, 'analysisComplete:', analysisComplete);
        if (buildings.length > 0 && !analysisComplete) {
            console.log('🔄 Starting analysis for', buildings.length, 'buildings');
            const timer = setTimeout(() => {
                console.log('📊 Generating summary stats...');
                const summaryData = BuildingDataService.generateSummaryStats(buildings);
                console.log('📈 Summary data generated:', summaryData);
                setSummaryStats(summaryData);
                setAnalysisComplete(true);
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [buildings, analysisComplete]);

    const handleBuildingSelect = (building: Building) => {
        navigate(`/building/${building.id}`);
    };

    // Debug logging
    console.log('🔍 Dashboard render - buildings.length:', buildings.length, 'loading:', loading, 'error:', error, 'analysisComplete:', analysisComplete);
    if (summaryStats) {
        console.log('📊 Summary stats object:', summaryStats);
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Presentation Mode Controls */}
            {!presentationMode && (
                <div className="bg-white border-b border-gray-200 p-4 sticky top-0 z-50">
                    <div className="max-w-7xl mx-auto flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <h2 className="text-xl font-bold text-gray-900">NYC Energy Retrofit Platform</h2>
                            <span className="text-sm text-gray-500">Investor Pitch Deck</span>
                        </div>
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => setPresentationMode(true)}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                            >
                                🎯 Start Presentation
                            </button>
                            <div className="text-sm text-gray-600">
                                {slideNames.length} slides
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Slide Navigation Bar (only in presentation mode) */}
            {presentationMode && (
                <>
                    {/* Top Navigation Bar */}
                    <div className="fixed top-0 left-0 right-0 bg-black bg-opacity-90 text-white p-4 z-50">
                        <div className="max-w-7xl mx-auto flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <button
                                    onClick={() => setPresentationMode(false)}
                                    className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-sm transition-colors"
                                >
                                    Exit Presentation
                                </button>
                                <div className="text-sm">
                                    Slide {currentSlide + 1} of {slideNames.length}
                                </div>
                                <div className="text-sm text-gray-300">
                                    {slideNames[currentSlide]}
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={prevSlide}
                                    disabled={currentSlide === 0}
                                    className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 px-3 py-1 rounded text-sm transition-colors"
                                >
                                    ← Previous
                                </button>
                                <button
                                    onClick={nextSlide}
                                    disabled={currentSlide === slideNames.length - 1}
                                    className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 px-3 py-1 rounded text-sm transition-colors"
                                >
                                    Next →
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Slide Thumbnails/Navigator */}
                    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 rounded-lg p-2 z-50">
                        <div className="flex space-x-1">
                            {slideNames.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => goToSlide(index)}
                                    className={`w-3 h-3 rounded-full transition-colors ${
                                        currentSlide === index 
                                            ? 'bg-blue-500' 
                                            : 'bg-gray-500 hover:bg-gray-400'
                                    }`}
                                    title={`Slide ${index + 1}: ${slideNames[index]}`}
                                />
                            ))}
                        </div>
                    </div>
                </>
            )}

            {/* Main Content Container with conditional slide visibility */}
            <div className={presentationMode ? "pt-16" : ""}>
                {/* SLIDE 0: TITLE & EXECUTIVE SUMMARY */}
                {(!presentationMode || currentSlide === 0) && (
                    <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 text-white">
                        <div className="max-w-7xl mx-auto px-4 py-20">
                            <div className="text-center mb-16">
                                <div className="mb-8">
                                    <h1 className="text-5xl md:text-7xl font-bold mb-4">
                                        NYC Energy Retrofit Platform
                                    </h1>
                                    <p className="text-2xl md:text-3xl text-emerald-400 font-semibold mb-6">
                                        Turning LL97 Compliance From Burden to Business Opportunity
                                    </p>
                                </div>
                        
                                <div className="max-w-5xl mx-auto mb-12">
                                    <p className="text-lg md:text-xl text-slate-200 mb-8">
                                        We're building the essential platform for NYC's $8.7B building energy compliance market, 
                                        connecting property owners with vetted contractors and financing to meet mandatory LL97 requirements.
                                    </p>
                                    
                                    {/* Fundraising Box */}
                                    <div className="bg-slate-800 border border-slate-600 rounded-xl p-6 mb-8 max-w-2xl mx-auto">
                                        <div className="text-emerald-400 text-lg font-semibold mb-2">SAFE Note Round</div>
                                        <div className="text-3xl font-bold text-white mb-2">$250K - $750K</div>
                                        <div className="text-slate-300">$3M-$5M Valuation Cap • 20% Discount</div>
                                    </div>
                                </div>
                        
                                {/* Key Metrics Highlights */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
                                    <div className="bg-slate-800 border border-slate-600 rounded-lg p-6">
                                        <div className="text-3xl md:text-4xl font-bold text-emerald-400 mb-2">$8.7B</div>
                                        <div className="text-sm md:text-base text-slate-300">Total Market Size</div>
                                    </div>
                                    <div className="bg-slate-800 border border-slate-600 rounded-lg p-6">
                                        <div className="text-3xl md:text-4xl font-bold text-emerald-400 mb-2">50K+</div>
                                        <div className="text-sm md:text-base text-slate-300">Target Buildings</div>
                                    </div>
                                    <div className="bg-slate-800 border border-slate-600 rounded-lg p-6">
                                        <div className="text-3xl md:text-4xl font-bold text-emerald-400 mb-2">$1.2M</div>
                                        <div className="text-sm md:text-base text-slate-300">Year 2 ARR Target</div>
                                    </div>
                                    <div className="bg-slate-800 border border-slate-600 rounded-lg p-6">
                                        <div className="text-3xl md:text-4xl font-bold text-emerald-400 mb-2">7</div>
                                        <div className="text-sm md:text-base text-slate-300">Expansion Cities</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Loading Status Section */}
                {loading && (
                    <div className="max-w-7xl mx-auto px-4 py-8">
                        <div className="bg-white rounded-lg shadow-lg p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">
                                Loading NYC Building Energy Data
                            </h2>
                            <div className="flex items-center space-x-3">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
                                <span className="text-gray-600">Processing 64,000+ NYC buildings...</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Error Section */}
                {error && (
                    <div className="max-w-7xl mx-auto px-4 py-8">
                        <div className="bg-gray-50 border border-gray-300 rounded-lg p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-2">Data Loading Error</h2>
                            <p className="text-gray-700">{error}</p>
                        </div>
                    </div>
                )}

                {/* Main Content */}
                {analysisComplete && !loading && (
                    <div className="max-w-7xl mx-auto px-4 py-8 space-y-12">
                        
                        {/* SLIDE 1: THE PROBLEM */}
                        {(!presentationMode || currentSlide === 1) && (
                            <div className="bg-white rounded-lg shadow-xl p-10">
                                <div className="text-center mb-10">
                                    <h2 className="text-4xl font-bold text-gray-900 mb-6">
                                        The Problem: $8.7B Compliance Crisis
                                    </h2>
                                    <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                                        NYC's Local Law 97 mandates building energy compliance by 2024, but property owners lack the tools, 
                                        connections, and expertise to navigate this complex, high-stakes transition.
                                    </p>
                                </div>
                                
                                {/* Problem Points */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
                                    <div className="text-center p-8 bg-red-50 rounded-xl border border-red-200">
                                        <div className="text-2xl mb-4">🚨</div>
                                        <h3 className="text-2xl font-bold text-red-800 mb-4">Information Gap</h3>
                                        <div className="text-3xl font-bold text-red-700 mb-3">73%</div>
                                        <p className="text-red-700">
                                            Buildings currently don't comply with LL97 limits. Property owners don't know their exact exposure or next steps.
                                        </p>
                                    </div>
                                    
                                    <div className="text-center p-8 bg-amber-50 rounded-xl border border-amber-200">
                                        <div className="text-2xl mb-4">🔍</div>
                                        <h3 className="text-2xl font-bold text-amber-800 mb-4">Service Discovery Problem</h3>
                                        <div className="text-3xl font-bold text-amber-700 mb-3">$2M+</div>
                                        <p className="text-amber-700">
                                            Property owners struggle to find vetted contractors, understand financing options, or navigate complex incentive programs.
                                        </p>
                                    </div>
                                    
                                    <div className="text-center p-8 bg-blue-50 rounded-xl border border-blue-200">
                                        <div className="text-2xl mb-4">💰</div>
                                        <h3 className="text-2xl font-bold text-blue-800 mb-4">Capital Access Barriers</h3>
                                        <div className="text-3xl font-bold text-blue-700 mb-3">$2M+ Projects</div>
                                        <p className="text-blue-700">
                                            Retrofit projects require significant upfront capital. CPACE financing exists but discovery and application processes are complex.
                                        </p>
                                    </div>
                                </div>
                                
                                {/* Market Impact Stats */}
                                <div className="bg-gray-100 rounded-xl p-8">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Market Impact by the Numbers</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-gray-900">50,000+</div>
                                            <div className="text-sm text-gray-600">Buildings Subject to LL97</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-red-600">$2.1B</div>
                                            <div className="text-sm text-gray-600">Annual Fine Exposure</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-amber-600">73%</div>
                                            <div className="text-sm text-gray-600">Buildings Currently Non-Compliant</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-blue-600">$8.7B</div>
                                            <div className="text-sm text-gray-600">Total Retrofit Investment Needed</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* SLIDE 2: THE SOLUTION */}
                        {(!presentationMode || currentSlide === 2) && (
                            <div className="bg-white rounded-lg shadow-xl p-10">
                                <div className="text-center mb-10">
                                    <h2 className="text-4xl font-bold text-gray-900 mb-6">
                                        Our Solution: The LL97 Compliance Platform
                                    </h2>
                                    <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                                        We've built the only comprehensive platform that connects property owners with vetted contractors, 
                                        CPACE financing, and incentive programs—turning compliance burdens into streamlined business opportunities.
                                    </p>
                                </div>
                                
                                {/* Solution Features */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
                                    <div className="p-8 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200">
                                        <div className="text-2xl mb-4">🎯</div>
                                        <h3 className="text-2xl font-bold text-emerald-800 mb-4">Smart Analysis</h3>
                                        <ul className="text-emerald-700 space-y-2">
                                            <li>• Real-time LL97 compliance calculations</li>
                                            <li>• Building-specific fine projections</li>
                                            <li>• Retrofit ROI analysis</li>
                                            <li>• Incentive program matching</li>
                                        </ul>
                                    </div>
                                    
                                    <div className="p-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                                        <div className="text-2xl mb-4">🤝</div>
                                        <h3 className="text-2xl font-bold text-blue-800 mb-4">Vetted Network</h3>
                                        <ul className="text-blue-700 space-y-2">
                                            <li>• 18 pre-screened contractors</li>
                                            <li>• CPACE financing connections</li>
                                            <li>• Transparent pricing & timelines</li>
                                            <li>• Direct connection facilitation</li>
                                        </ul>
                                    </div>
                                    
                                    <div className="p-8 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200">
                                        <div className="text-2xl mb-4">💼</div>
                                        <h3 className="text-2xl font-bold text-purple-800 mb-4">Revenue Generation</h3>
                                        <ul className="text-purple-700 space-y-2">
                                            <li>• 3% contractor referral commissions</li>
                                            <li>• 0.75% CPACE financing fees</li>
                                            <li>• SaaS subscription tiers</li>
                                            <li>• Premium consulting services</li>
                                        </ul>
                                    </div>
                                </div>
                                
                                {/* Platform Demo Access */}
                                <div className="bg-slate-50 rounded-xl p-8">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Live Platform Demo</h3>
                                    <div className="flex flex-wrap justify-center gap-4">
                                        <Link
                                            to="/building-analysis"
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 px-8 rounded-lg transition-colors shadow-md"
                                        >
                                            📊 Building Analysis Demo
                                        </Link>
                                        <Link
                                            to="/target-properties"
                                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-8 rounded-lg transition-colors shadow-md"
                                        >
                                            🎯 Target Properties Portfolio
                                        </Link>
                                        <Link
                                            to="/connection-hub"
                                            className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 px-8 rounded-lg transition-colors shadow-md"
                                        >
                                            🤝 Service Provider Network
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* SLIDE 3: MARKET OPPORTUNITY & BUSINESS MODEL */}
                        {(!presentationMode || currentSlide === 3) && (
                            <div className="bg-white rounded-lg shadow-xl p-10">
                                <div className="text-center mb-10">
                                    <h2 className="text-4xl font-bold text-gray-900 mb-6">
                                        Massive Market Opportunity with Multiple Revenue Streams
                                    </h2>
                                    <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                                        LL97 creates a mandated $8.7B compliance market with predictable revenue streams. 
                                        Our platform captures value through multiple channels with strong unit economics.
                                    </p>
                                </div>
                                
                                {/* Market Size Breakdown */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-10">
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Market Sizing & Opportunity</h3>
                                        <div className="space-y-4">
                                            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="font-medium">SaaS Subscriptions ($200-2K/month)</span>
                                                    <span className="font-bold text-emerald-700">$47K/month</span>
                                                </div>
                                                <div className="text-sm text-emerald-600">Predictable recurring revenue - customer LTV</div>
                                            </div>
                                            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="font-medium">Contractor Referrals (3% commission)</span>
                                                    <span className="font-bold text-emerald-700">$15K/month</span>
                                                </div>
                                                <div className="text-sm text-emerald-600">Transaction-based - scales with market growth</div>
                                            </div>
                                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="font-medium">CPACE Financing (0.75% fees)</span>
                                                    <span className="font-bold text-blue-700">$8K/month</span>
                                                </div>
                                                <div className="text-sm text-blue-600">High-value transactions - $2M+ projects</div>
                                            </div>
                                            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="font-medium">Premium Consulting</span>
                                                    <span className="font-bold text-purple-700">$12K/month</span>
                                                </div>
                                                <div className="text-sm text-purple-600">White-glove service for enterprise clients</div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Unit Economics</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="text-center">
                                                <div className="text-3xl font-bold text-emerald-600">85%</div>
                                                <div className="text-sm text-gray-600">Gross Margin</div>
                                                <div className="text-xs text-gray-500 mt-1">SaaS + commissions</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-3xl font-bold text-blue-600">3.8x</div>
                                                <div className="text-sm text-gray-600">LTV/CAC Ratio</div>
                                                <div className="text-xs text-gray-500 mt-1">Strong unit economics</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-3xl font-bold text-amber-600">7%</div>
                                                <div className="text-sm text-gray-600">Annual Churn</div>
                                                <div className="text-xs text-gray-500 mt-1">Regulatory lock-in</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* SLIDE 4: TRACTION & VALIDATION */}
                        {(!presentationMode || currentSlide === 4) && (
                            <div className="bg-white rounded-lg shadow-xl p-10">
                                <div className="text-center mb-10">
                                    <h2 className="text-4xl font-bold text-gray-900 mb-6">
                                        Strong Traction & Market Validation
                                    </h2>
                                    <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                                        We've built a working platform, secured initial customers, and validated 
                                        our business model with real NYC property owners and service providers.
                                    </p>
                                </div>
                                
                                {/* Traction Metrics */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
                                    <div className="text-center p-8 bg-emerald-50 rounded-xl border border-emerald-200">
                                        <div className="text-2xl mb-4">🏢</div>
                                        <h3 className="text-2xl font-bold text-emerald-800 mb-4">Platform Built</h3>
                                        <div className="text-3xl font-bold text-emerald-700 mb-3">64K+</div>
                                        <p className="text-emerald-700">
                                            NYC buildings analyzed with real LL97 compliance calculations and retrofit recommendations.
                                        </p>
                                    </div>
                                    
                                    <div className="text-center p-8 bg-blue-50 rounded-xl border border-blue-200">
                                        <div className="text-2xl mb-4">🤝</div>
                                        <h3 className="text-2xl font-bold text-blue-800 mb-4">Service Network</h3>
                                        <div className="text-3xl font-bold text-blue-700 mb-3">18</div>
                                        <p className="text-blue-700">
                                            Vetted contractors and financing partners ready to serve our platform users.
                                        </p>
                                    </div>
                                    
                                    <div className="text-center p-8 bg-purple-50 rounded-xl border border-purple-200">
                                        <div className="text-2xl mb-4">💰</div>
                                        <h3 className="text-2xl font-bold text-purple-800 mb-4">Market Interest</h3>
                                        <div className="text-3xl font-bold text-purple-700 mb-3">$15M+</div>
                                        <p className="text-purple-700">
                                            Pipeline of projects from early customer conversations and partnership agreements.
                                        </p>
                                    </div>
                                </div>
                                
                                {/* Customer Validation */}
                                <div className="bg-gray-100 rounded-xl p-8">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Customer Validation</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="bg-white p-6 rounded-lg">
                                            <h4 className="text-lg font-bold text-gray-900 mb-3">Property Owners</h4>
                                            <ul className="text-gray-700 space-y-2">
                                                <li>• 73% of buildings don't comply with LL97</li>
                                                <li>• Average potential fine: $42K/year</li>
                                                <li>• 85% lack retrofit knowledge</li>
                                                <li>• Willing to pay $500-2K/month for platform</li>
                                            </ul>
                                        </div>
                                        <div className="bg-white p-6 rounded-lg">
                                            <h4 className="text-lg font-bold text-gray-900 mb-3">Service Providers</h4>
                                            <ul className="text-gray-700 space-y-2">
                                                <li>• Struggle with lead generation</li>
                                                <li>• Pay 5-15% for qualified referrals</li>
                                                <li>• Need project pipeline visibility</li>
                                                <li>• Value platform's vetting process</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* SLIDE 5: COMPETITIVE ADVANTAGE & GO-TO-MARKET */}
                        {(!presentationMode || currentSlide === 5) && (
                            <div className="bg-white rounded-lg shadow-xl p-10">
                                <div className="text-center mb-10">
                                    <h2 className="text-4xl font-bold text-gray-900 mb-6">
                                        Competitive Advantage & Go-to-Market Strategy
                                    </h2>
                                    <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                                        We have first-mover advantage in a mandated market with multiple defensive moats 
                                        and a proven customer acquisition strategy.
                                    </p>
                                </div>
                                
                                {/* Competitive Advantages */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-10">
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Competitive Moats</h3>
                                        <div className="space-y-4">
                                            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                                                <h4 className="font-bold text-emerald-800 mb-2">🎯 First-Mover Advantage</h4>
                                                <p className="text-emerald-700 text-sm">Only comprehensive LL97 platform with real building data and service network</p>
                                            </div>
                                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                                <h4 className="font-bold text-blue-800 mb-2">🏢 Proprietary Building Data</h4>
                                                <p className="text-blue-700 text-sm">64K+ buildings analyzed with compliance calculations and retrofit modeling</p>
                                            </div>
                                            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                                                <h4 className="font-bold text-purple-800 mb-2">🤝 Network Effects</h4>
                                                <p className="text-purple-700 text-sm">More users attract more service providers, creating value flywheel</p>
                                            </div>
                                            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                                                <h4 className="font-bold text-amber-800 mb-2">🔒 Regulatory Lock-in</h4>
                                                <p className="text-amber-700 text-sm">LL97 compliance is mandatory, creating sticky customer relationships</p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Go-to-Market Strategy</h3>
                                        <div className="space-y-4">
                                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <h4 className="font-bold text-gray-800 mb-2">Phase 1: Direct Sales (Months 1-6)</h4>
                                                <p className="text-gray-700 text-sm">Target 25 largest property management companies in NYC</p>
                                            </div>
                                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <h4 className="font-bold text-gray-800 mb-2">Phase 2: Digital Marketing (Months 4-12)</h4>
                                                <p className="text-gray-700 text-sm">Content marketing, SEO, and targeted ads to property owners</p>
                                            </div>
                                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <h4 className="font-bold text-gray-800 mb-2">Phase 3: Partnerships (Months 6-18)</h4>
                                                <p className="text-gray-700 text-sm">Strategic alliances with law firms, consultants, and trade associations</p>
                                            </div>
                                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <h4 className="font-bold text-gray-800 mb-2">Phase 4: Expansion (Year 2+)</h4>
                                                <p className="text-gray-700 text-sm">Scale to 7 additional cities with similar building energy laws</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* SLIDE 6: FINANCIAL PROJECTIONS & GROWTH PLAN */}
                        {(!presentationMode || currentSlide === 6) && (
                            <div className="bg-white rounded-lg shadow-xl p-10">
                                <div className="text-center mb-10">
                                    <h2 className="text-4xl font-bold text-gray-900 mb-6">
                                        Financial Projections & Growth Plan
                                    </h2>
                                    <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                                        Strong unit economics and multiple revenue streams drive predictable growth 
                                        to $1.2M ARR by Year 2 with clear path to profitability.
                                    </p>
                                </div>
                                
                                {/* Financial Projections */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-10">
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Revenue Projections</h3>
                                        <div className="space-y-4">
                                            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="font-medium">Year 1 ARR</span>
                                                    <span className="font-bold text-emerald-700">$240K</span>
                                                </div>
                                                <div className="text-sm text-emerald-600">25 customers @ $800/month average</div>
                                            </div>
                                            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="font-medium">Year 2 ARR</span>
                                                    <span className="font-bold text-emerald-700">$1.2M</span>
                                                </div>
                                                <div className="text-sm text-emerald-600">100 customers + commission revenue</div>
                                            </div>
                                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="font-medium">Year 3 ARR</span>
                                                    <span className="font-bold text-blue-700">$3.5M</span>
                                                </div>
                                                <div className="text-sm text-blue-600">Multi-city expansion + enterprise deals</div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Key Metrics</h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="text-center p-4 bg-gray-50 rounded-lg">
                                                <div className="text-2xl font-bold text-gray-900">85%</div>
                                                <div className="text-sm text-gray-600">Gross Margin</div>
                                            </div>
                                            <div className="text-center p-4 bg-gray-50 rounded-lg">
                                                <div className="text-2xl font-bold text-gray-900">7%</div>
                                                <div className="text-sm text-gray-600">Monthly Churn</div>
                                            </div>
                                            <div className="text-center p-4 bg-gray-50 rounded-lg">
                                                <div className="text-2xl font-bold text-gray-900">$18K</div>
                                                <div className="text-sm text-gray-600">Customer LTV</div>
                                            </div>
                                            <div className="text-center p-4 bg-gray-50 rounded-lg">
                                                <div className="text-2xl font-bold text-gray-900">$4.8K</div>
                                                <div className="text-sm text-gray-600">Customer CAC</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Growth Plan */}
                                <div className="bg-gray-100 rounded-xl p-8">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Growth Plan Timeline</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div className="bg-white p-6 rounded-lg">
                                            <h4 className="text-lg font-bold text-gray-900 mb-3">Year 1: Foundation</h4>
                                            <ul className="text-gray-700 space-y-1 text-sm">
                                                <li>• Complete NYC rollout</li>
                                                <li>• 25 paying customers</li>
                                                <li>• $240K ARR</li>
                                                <li>• Break-even by Q4</li>
                                            </ul>
                                        </div>
                                        <div className="bg-white p-6 rounded-lg">
                                            <h4 className="text-lg font-bold text-gray-900 mb-3">Year 2: Scale</h4>
                                            <ul className="text-gray-700 space-y-1 text-sm">
                                                <li>• 100 customers</li>
                                                <li>• $1.2M ARR</li>
                                                <li>• Expand to Boston</li>
                                                <li>• Series A fundraise</li>
                                            </ul>
                                        </div>
                                        <div className="bg-white p-6 rounded-lg">
                                            <h4 className="text-lg font-bold text-gray-900 mb-3">Year 3: Expansion</h4>
                                            <ul className="text-gray-700 space-y-1 text-sm">
                                                <li>• 7 cities total</li>
                                                <li>• $3.5M ARR</li>
                                                <li>• 300+ customers</li>
                                                <li>• Market leadership</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* SLIDE 7: TEAM & USE OF FUNDS */}
                        {(!presentationMode || currentSlide === 7) && (
                            <div className="bg-white rounded-lg shadow-xl p-10">
                                <div className="text-center mb-10">
                                    <h2 className="text-4xl font-bold text-gray-900 mb-6">
                                        Team & Use of Funds
                                    </h2>
                                    <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                                        Experienced team with domain expertise and proven execution capability. 
                                        Funding will accelerate customer acquisition and product development.
                                    </p>
                                </div>
                                
                                {/* Team Section */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-10">
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Core Team</h3>
                                        <div className="space-y-4">
                                            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                                                <h4 className="font-bold text-emerald-800 mb-2">CEO/Founder</h4>
                                                <p className="text-emerald-700 text-sm">10+ years in PropTech and energy efficiency. Former building energy consultant.</p>
                                            </div>
                                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                                <h4 className="font-bold text-blue-800 mb-2">CTO/Co-Founder</h4>
                                                <p className="text-blue-700 text-sm">15+ years building scalable platforms. Former lead engineer at energy analytics startup.</p>
                                            </div>
                                            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                                                <h4 className="font-bold text-purple-800 mb-2">Head of Sales</h4>
                                                <p className="text-purple-700 text-sm">8+ years selling B2B SaaS to property management companies and real estate firms.</p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Advisory Board</h3>
                                        <div className="space-y-4">
                                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <h4 className="font-bold text-gray-800 mb-2">Real Estate Industry Veteran</h4>
                                                <p className="text-gray-700 text-sm">Former VP at major NYC property management firm with 500+ buildings</p>
                                            </div>
                                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <h4 className="font-bold text-gray-800 mb-2">CPACE Financing Expert</h4>
                                                <p className="text-gray-700 text-sm">Structured $100M+ in building energy financing deals</p>
                                            </div>
                                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <h4 className="font-bold text-gray-800 mb-2">Former NYC Policy Official</h4>
                                                <p className="text-gray-700 text-sm">Deep knowledge of Local Law 97 implementation and regulatory landscape</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Use of Funds */}
                                <div className="bg-gray-100 rounded-xl p-8">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Use of Funds ($250K - $750K)</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                        <div className="text-center">
                                            <div className="text-3xl font-bold text-emerald-600">40%</div>
                                            <div className="text-sm text-gray-600 mb-2">Sales & Marketing</div>
                                            <div className="text-xs text-gray-500">Customer acquisition, sales team, marketing campaigns</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-3xl font-bold text-blue-600">30%</div>
                                            <div className="text-sm text-gray-600 mb-2">Product Development</div>
                                            <div className="text-xs text-gray-500">Platform features, integrations, mobile app</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-3xl font-bold text-purple-600">20%</div>
                                            <div className="text-sm text-gray-600 mb-2">Operations</div>
                                            <div className="text-xs text-gray-500">Infrastructure, legal, compliance</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-3xl font-bold text-amber-600">10%</div>
                                            <div className="text-sm text-gray-600 mb-2">Contingency</div>
                                            <div className="text-xs text-gray-500">Buffer for unexpected opportunities</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* SLIDE 8: THE ASK & NEXT STEPS */}
                        {(!presentationMode || currentSlide === 8) && (
                            <div className="bg-white rounded-lg shadow-xl p-10">
                                <div className="text-center mb-10">
                                    <h2 className="text-4xl font-bold text-gray-900 mb-6">
                                        The Ask & Next Steps
                                    </h2>
                                    <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                                        Join us in building the essential infrastructure for NYC's building energy transition. 
                                        Strong returns with positive environmental and economic impact.
                                    </p>
                                </div>
                                
                                {/* Investment Terms */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-10">
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Investment Terms</h3>
                                        <div className="space-y-4">
                                            <div className="p-6 bg-emerald-50 rounded-lg border border-emerald-200">
                                                <h4 className="font-bold text-emerald-800 mb-2">SAFE Note Round</h4>
                                                <div className="text-emerald-700 space-y-2">
                                                    <p><span className="font-medium">Amount:</span> $250K - $750K</p>
                                                    <p><span className="font-medium">Valuation Cap:</span> $3M - $5M</p>
                                                    <p><span className="font-medium">Discount:</span> 20%</p>
                                                    <p><span className="font-medium">Pro-rata Rights:</span> Yes</p>
                                                </div>
                                            </div>
                                            <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
                                                <h4 className="font-bold text-blue-800 mb-2">Series A Timeline</h4>
                                                <div className="text-blue-700 space-y-2">
                                                    <p><span className="font-medium">Timeline:</span> 12-18 months</p>
                                                    <p><span className="font-medium">Target Size:</span> $2M - $4M</p>
                                                    <p><span className="font-medium">Use:</span> Multi-city expansion</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Invest Now</h3>
                                        <div className="space-y-4">
                                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <h4 className="font-bold text-gray-800 mb-2">🎯 First-Mover Advantage</h4>
                                                <p className="text-gray-700 text-sm">Capture market before competition enters mandated compliance space</p>
                                            </div>
                                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <h4 className="font-bold text-gray-800 mb-2">📈 Proven Traction</h4>
                                                <p className="text-gray-700 text-sm">Working platform, customer validation, service provider network</p>
                                            </div>
                                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <h4 className="font-bold text-gray-800 mb-2">💰 Strong Unit Economics</h4>
                                                <p className="text-gray-700 text-sm">85% gross margins, 3.8x LTV/CAC, low churn</p>
                                            </div>
                                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <h4 className="font-bold text-gray-800 mb-2">🌱 ESG Impact</h4>
                                                <p className="text-gray-700 text-sm">Accelerate building decarbonization and climate goals</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Next Steps */}
                                <div className="bg-slate-900 text-white rounded-xl p-8">
                                    <h3 className="text-2xl font-bold mb-6 text-center">Next Steps</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div className="text-center">
                                            <div className="text-2xl mb-4">📞</div>
                                            <h4 className="font-bold mb-2">Schedule Follow-up</h4>
                                            <p className="text-slate-300 text-sm">Deep dive into financials, market size, and expansion strategy</p>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-2xl mb-4">🔍</div>
                                            <h4 className="font-bold mb-2">Due Diligence</h4>
                                            <p className="text-slate-300 text-sm">Access to data room, customer references, and detailed projections</p>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-2xl mb-4">🤝</div>
                                            <h4 className="font-bold mb-2">Investment Decision</h4>
                                            <p className="text-slate-300 text-sm">Term sheet, legal documentation, and funding close</p>
                                        </div>
                                    </div>
                                    
                                    <div className="text-center mt-8">
                                        <div className="text-emerald-400 text-lg font-semibold mb-2">Contact Information</div>
                                        <p className="text-slate-300">info@ll97plan.com • 917-674-5720</p>
                                        <p className="text-slate-300">www.nycenergyretrofit.com</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Building Data Analysis */}
                        {summaryStats && (
                            <div className="bg-white rounded-lg shadow-lg p-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                                    NYC Building Data Analysis ({buildings.length.toLocaleString()} Buildings Loaded)
                                </h2>
                                
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-gray-900">
                                            {summaryStats.buildingsOverLimit?.toLocaleString() || '0'}
                                        </div>
                                        <div className="text-sm text-gray-600">Buildings Over LL97 Limit</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-slate-700">
                                            {summaryStats.totalLL97Fines?.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) || '$0'}
                                        </div>
                                        <div className="text-sm text-gray-600">Annual Fines Potential</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-amber-600">
                                            {summaryStats.viableProspects?.toLocaleString() || '0'}
                                        </div>
                                        <div className="text-sm text-gray-600">Retrofit Candidates</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-emerald-600">
                                            {summaryStats.totalPotentialSavings?.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) || '$0'}
                                        </div>
                                        <div className="text-sm text-gray-600">Market Opportunity</div>
                                    </div>
                                </div>
                                
                                <div className="flex justify-center">
                                    <Link
                                        to="/building-analysis"
                                        className="bg-slate-700 hover:bg-slate-800 text-white font-bold py-3 px-8 rounded-lg transition-colors shadow-md"
                                    >
                                        View Detailed Analysis →
                                    </Link>
                                </div>
                            </div>
                        )}

                        {/* How It Works */}
                        <div className="bg-white rounded-lg shadow-lg p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">How The Platform Works</h2>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                <div className="text-center">
                                    <div className="bg-slate-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                                        <span className="text-slate-700 font-bold text-lg">1</span>
                                    </div>
                                    <h3 className="font-semibold text-gray-900 mb-2">Data Analysis</h3>
                                    <p className="text-sm text-gray-600">
                                        Calculate potential fines, retrofit costs, and savings to prioritize investments for maximum return.
                                    </p>
                                </div>
                                
                                <div className="text-center">
                                    <div className="bg-slate-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                                        <span className="text-slate-700 font-bold text-lg">2</span>
                                    </div>
                                    <h3 className="font-semibold text-gray-900 mb-2">Service Connections</h3>
                                    <p className="text-sm text-gray-600">
                                        Connect with vetted contractors and CPACE financing providers for competitive quotes and terms.
                                    </p>
                                </div>
                                
                                <div className="text-center">
                                    <div className="bg-slate-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                                        <span className="text-slate-700 font-bold text-lg">3</span>
                                    </div>
                                    <h3 className="font-semibold text-gray-900 mb-2">Project Management</h3>
                                    <p className="text-sm text-gray-600">
                                        Track progress, manage payments, and ensure compliance through project completion.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Pitch;
